import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, Share2, Bookmark, Play, Pause, MoreHorizontal, Download, Pencil, Trash2, Link as LinkIcon, Flag } from 'lucide-react';
import TipButton from './TipButton';
import ChalkButton from './ChalkButton';
import { Link } from 'react-router-dom';
import { fileUrl } from '../lib/api';
import api from '../lib/api';
import { usePlayer } from '../context/PlayerContext';
import { useAuth } from '../context/AuthContext';
import { Waveform } from './Waveform';
import { toast } from 'sonner';

const formatTime = (s) => {
  if (!s || isNaN(s)) return '--:--';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true`;

export const PostCard = ({ post, onUpdate, onCommentOpen, onDelete }) => {
  const { currentTrack, isPlaying, playTrack, progress, duration } = usePlayer();
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [isSaved, setIsSaved] = useState(post.is_saved);
  const [sharesCount, setSharesCount] = useState(post.shares_count);
  const [heartAnim, setHeartAnim] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title || '');
  const [editDesc, setEditDesc] = useState(post.description || '');
  const [busy, setBusy] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const menuRef = useRef(null);
  const isOwner = user && user.user_id === post.user_id;

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); };
  }, [menuOpen]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.delete(`/posts/${post.id}`);
      setDeleted(true);
      toast.success('Post deleted');
      onDelete?.(post.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) { toast.error('Title cannot be empty'); return; }
    setBusy(true);
    try {
      await api.patch(`/posts/${post.id}`, { title: editTitle.trim(), description: editDesc });
      toast.success('Post updated');
      post.title = editTitle.trim();
      post.description = editDesc;
      setEditing(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/post/${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.success(url);
    }
    setMenuOpen(false);
  };

  const reportPost = () => {
    toast.success('Report received — we\'ll take a look');
    setMenuOpen(false);
  };

  const isCurrent = currentTrack?.id === post.id;
  const isThisPlaying = isCurrent && isPlaying;

  const coverSrc = post.cover_path
    ? fileUrl(post.cover_path)
    : 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?crop=entropy&cs=srgb&fm=jpg&q=85&w=600';

  const handleLike = async () => {
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
    setHeartAnim(true);
    setTimeout(() => setHeartAnim(false), 400);
    try {
      await api.post(`/posts/${post.id}/like`);
    } catch {
      setIsLiked(isLiked);
      setLikesCount(post.likes_count);
      toast.error('Failed to update like');
    }
  };

  const handleSave = async () => {
    setIsSaved(!isSaved);
    try {
      await api.post(`/posts/${post.id}/save`);
      toast.success(isSaved ? 'Removed from saved' : 'Saved to your library');
    } catch {
      setIsSaved(isSaved);
    }
  };

  const handleDownload = () => {
    // Hit the ?download=1 variant so the browser saves the file with a proper
    // filename instead of streaming inline into a new tab.
    const url = `${fileUrl(post.audio_path)}?download=1`;
    // Use a hidden anchor rather than window.open so mobile Safari cooperates.
    const a = document.createElement('a');
    a.href = url;
    a.download = post.title || 'raw-mix';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Download started');
  };

  const handleShare = async () => {
    try {
      await api.post(`/posts/${post.id}/share`);
      setSharesCount(prev => prev + 1);
    } catch {
      toast.error('Share failed');
      return;
    }
    // Clipboard is best-effort — a blocked/unavailable clipboard shouldn't
    // undo the successful share on the server.
    const url = `${window.location.origin}/post/${post.id}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      } else {
        toast.success('Shared');
      }
    } catch {
      toast.success('Shared');
    }
  };

  const handlePlay = () => {
    playTrack(post);
  };

  if (deleted) return null;
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="post-card p-5 md:p-6"
      data-testid={`post-card-${post.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to={`/profile/${post.author?.user_id}`}
          className="flex items-center gap-3 group"
          data-testid={`post-author-link-${post.id}`}
        >
          <img
            src={post.author?.avatar_path ? fileUrl(post.author.avatar_path) : avatarFallback(post.author?.name)}
            alt=""
            className="w-11 h-11 rounded-full object-cover border border-white/10"
          />
          <div>
            <div className="text-white font-bold text-sm group-hover:text-[#FFB000] transition-colors font-heading">
              {post.author?.name || 'Unknown'}
            </div>
            <div className="text-[#A1A1AA] text-xs flex items-center gap-1.5 flex-wrap">
              <span>@{post.author?.username}</span>
              <span className="opacity-40">·</span>
              <span>{post.genre}</span>
              {post.bpm && (
                <>
                  <span className="opacity-40">·</span>
                  <span className="font-mono text-[#FFB000]">{post.bpm} BPM</span>
                </>
              )}
              {post.musical_key && (
                <>
                  <span className="opacity-40">·</span>
                  <span className="font-mono text-[#FFB000]">{post.musical_key}</span>
                </>
              )}
            </div>
          </div>
        </Link>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="text-[#A1A1AA] hover:text-white p-2"
            data-testid={`post-more-${post.id}`}
            aria-label="More options"
          >
            <MoreHorizontal size={18} />
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12 }}
                className="absolute right-0 top-full mt-1 z-30 min-w-[180px] rounded-xl bg-[#0d0d0d] border border-[#FFB000]/25 shadow-[0_8px_24px_rgba(0,0,0,0.7)] py-1 overflow-hidden"
                data-testid={`post-menu-${post.id}`}
              >
                {isOwner ? (
                  <>
                    <button
                      onClick={() => { setEditing(true); setMenuOpen(false); }}
                      className="w-full text-left px-3 py-2.5 text-sm text-white hover:bg-[#FFB000]/10 flex items-center gap-2 transition-colors"
                      data-testid={`post-edit-${post.id}`}
                    >
                      <Pencil size={14} className="text-[#FFB000]" /> Edit post
                    </button>
                    <button
                      onClick={copyLink}
                      className="w-full text-left px-3 py-2.5 text-sm text-white hover:bg-[#FFB000]/10 flex items-center gap-2 transition-colors"
                      data-testid={`post-copy-link-${post.id}`}
                    >
                      <LinkIcon size={14} className="text-[#FFB000]" /> Copy link
                    </button>
                    <div className="h-px bg-white/10 my-1" />
                    <button
                      onClick={handleDelete}
                      disabled={busy}
                      className="w-full text-left px-3 py-2.5 text-sm text-[#EF4444] hover:bg-[#EF4444]/10 flex items-center gap-2 transition-colors disabled:opacity-50"
                      data-testid={`post-delete-${post.id}`}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={copyLink}
                      className="w-full text-left px-3 py-2.5 text-sm text-white hover:bg-[#FFB000]/10 flex items-center gap-2 transition-colors"
                      data-testid={`post-copy-link-${post.id}`}
                    >
                      <LinkIcon size={14} className="text-[#FFB000]" /> Copy link
                    </button>
                    <button
                      onClick={reportPost}
                      className="w-full text-left px-3 py-2.5 text-sm text-[#EF4444] hover:bg-[#EF4444]/10 flex items-center gap-2 transition-colors"
                      data-testid={`post-report-${post.id}`}
                    >
                      <Flag size={14} /> Report
                    </button>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Inline edit form — replaces title/description while editing */}
      {editing ? (
        <div className="mb-4 space-y-2" data-testid={`post-edit-form-${post.id}`}>
          <input
            type="text"
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            placeholder="Title"
            className="w-full bg-black/60 border border-[#FFB000]/25 rounded-xl px-3 py-2 text-white text-lg font-bold focus:outline-none focus:border-[#FFB000]"
            data-testid={`post-edit-title-${post.id}`}
          />
          <textarea
            value={editDesc}
            onChange={e => setEditDesc(e.target.value)}
            rows={2}
            placeholder="Description"
            className="w-full bg-black/60 border border-[#FFB000]/25 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FFB000] resize-none"
            data-testid={`post-edit-desc-${post.id}`}
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveEdit}
              disabled={busy}
              className="btn-pill btn-primary text-xs disabled:opacity-50"
              data-testid={`post-edit-save-${post.id}`}
            >
              {busy ? 'Saving…' : 'Save changes'}
            </button>
            <button
              onClick={() => { setEditing(false); setEditTitle(post.title || ''); setEditDesc(post.description || ''); }}
              className="btn-pill btn-outline text-xs"
              data-testid={`post-edit-cancel-${post.id}`}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <></>
      )}

      {/* Title */}
      {!editing && (
        <>
          <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-2 leading-tight tracking-tight">
            {post.title}
          </h3>
          {post.description && (
            <p className="text-[#A1A1AA] text-sm mb-4 leading-relaxed line-clamp-2">
              {post.description}
            </p>
          )}
        </>
      )}

      {/* Cover + Play */}
      {post.media_type === 'video' ? (
        <div className="relative rounded-2xl overflow-hidden mb-4 bg-black" data-testid={`post-video-${post.id}`}>
          <video
            src={fileUrl(post.audio_path)}
            poster={post.cover_path ? fileUrl(post.cover_path) : undefined}
            controls
            preload="metadata"
            playsInline
            className="w-full max-h-[520px] object-contain"
          />
          {post.tags?.length > 0 && (
            <div className="absolute top-4 left-4 flex flex-wrap gap-1.5 max-w-[70%] pointer-events-none">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[#FFB000] text-[10px] font-bold tracking-wide uppercase"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="relative rounded-2xl overflow-hidden mb-4 group">
          <img
            src={coverSrc}
            alt={post.title}
            className="w-full h-72 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <button
            onClick={handlePlay}
            className="absolute bottom-4 left-4 w-14 h-14 rounded-full bg-[#FFB000] text-black flex items-center justify-center shadow-[0_0_20px_rgba(255,176,0,0.5)] hover:bg-[#E69E00] transition-all active:scale-95"
            data-testid={`post-play-${post.id}`}
            aria-label={isThisPlaying ? 'Pause' : 'Play'}
          >
            {isThisPlaying ? <Pause size={22} fill="black" /> : <Play size={22} fill="black" className="ml-0.5" />}
          </button>
          <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-black/60 backdrop-blur text-white text-xs font-mono">
            {isCurrent ? formatTime(progress) : '0:00'} / {isCurrent && duration ? formatTime(duration) : formatTime(post.duration)}
          </div>
          {post.tags?.length > 0 && (
            <div className="absolute top-4 left-4 flex flex-wrap gap-1.5 max-w-[70%]">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[#FFB000] text-[10px] font-bold tracking-wide uppercase"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Waveform (audio only) */}
      {post.media_type !== 'video' && (
        <div className="mb-4">
          <Waveform
            isPlaying={isThisPlaying}
            progress={isCurrent ? progress : 0}
            duration={isCurrent ? duration : (post.duration || 180)}
            bars={72}
            peaks={post.waveform}
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 md:gap-2">
          <button
            onClick={handleLike}
            className={`flex items-center gap-2 btn-ghost ${isLiked ? 'text-[#EF4444]' : ''} ${heartAnim ? 'heart-pop' : ''}`}
            data-testid={`post-like-${post.id}`}
          >
            <Heart size={18} fill={isLiked ? '#EF4444' : 'none'} />
            <span className="text-sm font-bold">{likesCount}</span>
          </button>
          <button
            onClick={() => onCommentOpen && onCommentOpen(post)}
            className="flex items-center gap-2 btn-ghost"
            data-testid={`post-comment-${post.id}`}
          >
            <MessageCircle size={18} />
            <span className="text-sm font-bold">{post.comments_count}</span>
          </button>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 btn-ghost"
            data-testid={`post-share-${post.id}`}
          >
            <Share2 size={18} />
            <span className="text-sm font-bold">{sharesCount}</span>
          </button>
          {post.author && (
            <TipButton recipient={post.author} postId={post.id} />
          )}
          {!post.is_chalk && post.media_type !== 'video' && (
            <ChalkButton post={post} />
          )}
        </div>
        <div className="flex items-center gap-3">
          {post.tip_total > 0 && (
            <div className="text-xs font-mono text-[#FFB000] font-bold" data-testid={`post-tips-${post.id}`}>
              £{(post.tip_total / 100).toFixed(2)} tipped
            </div>
          )}
          <button
            onClick={handleDownload}
            className="btn-ghost"
            data-testid={`post-download-${post.id}`}
            aria-label="Download mix"
            title="Download this mix"
          >
            <Download size={18} />
          </button>
          <button
            onClick={handleSave}
            className={`btn-ghost ${isSaved ? 'text-[#FFB000]' : ''}`}
            data-testid={`post-save-${post.id}`}
            aria-label={isSaved ? 'Unsave' : 'Save'}
          >
            <Bookmark size={18} fill={isSaved ? '#FFB000' : 'none'} />
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export default PostCard;
